import { Component, input, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { SelectControl } from '../../models/OptionDropdownSelect';
import { TaxRemittance, WireInfo } from '../../models/wire/WireInfo';
import { TextInputModel } from '../../../shared/components/controls/text-input-control/TextInputModel';
import { Subject, takeUntil } from 'rxjs';
import { MONTH_LIST } from '../../models/wire/MonthList';
import { selectWireInfo } from '../../reducers';
import { Store } from '@ngrx/store';
import { createControl } from '../../utilities/formUtilities';
import { numeralsOnly, taxPayerAllowedChars, taxTypeAllowedChars, taxTypeCodeValidation } from '../../../shared/utilities/regex-patterns';
import { Constants, taxPayerIdErrorMap, taxTypeCodeErrorMap } from '../../../shared/utilities/Constants';
import { selectStandingAuthWireInfo } from '../../../standing-authorizations/reducers';
import { selectIsCreateStandingAuth } from '../../../shared/reducers';
import { MaskAccountNumberPipe } from '../../../shared/pipes/mask-account-number.pipe';

@Component({
  standalone: false,
  selector: 'mm-wire-irs-tax-details',
  templateUrl: './wire-irs-tax-details.component.html',
  styleUrl: './wire-irs-tax-details.component.scss'
})
export class WireIrsTaxDetailsComponent implements OnInit, OnDestroy {
  @Input() parentForm: UntypedFormGroup;
  @Input() taxRemittance: TaxRemittance;
  @Input() useSloa: boolean = false;
  @Input() isFromCreateTransaction: boolean;
  destroy$ = new Subject<void>();
  taxForm: FormGroup;
  isIrsAba: boolean;
  isCreateStandingAuthPage: boolean;
   
  private readonly controlNames = {
    taxPayerId: 'taxPayerId',
    taxPayerIdMasked: 'taxPayerIdMasked',
    taxTypeCode: 'taxTypeCode',
    taxYear: 'taxYear',
    taxMonth: 'taxMonth'
  }

  taxPayerAllowedChars = taxPayerAllowedChars;
  taxTypeCodeAllowedChars = taxTypeAllowedChars;
  taxPayerId: TextInputModel = {
    controlName: this.controlNames.taxPayerId,
    label: 'Taxpayer ID',
    hasGreyText: true,
    greyText: '(TIN or EIN)',
    htmlMaxLength: 9,
    allowedChars: this.taxPayerAllowedChars
  };

  taxPayerIdMasked: TextInputModel = {
    controlName: this.controlNames.taxPayerIdMasked,
    label: 'Taxpayer ID',
    hasGreyText: true,
    greyText: '(TIN or EIN)',
    htmlMaxLength: 9,
    allowedChars: this.taxPayerAllowedChars
  };
  
  taxTypeCode: TextInputModel = {
    controlName: this.controlNames.taxTypeCode,
    label: 'Tax type code',
    hasGreyText: false,
    htmlMaxLength: 5,
    allowedChars: this.taxTypeCodeAllowedChars,
    hasIconTooltip: true,
    tooltip: {
      position: 'right',
      containsLink: false,
      html: Constants.wireIrsTooltip  
    }
  };

  taxMonthDropDown: SelectControl = {
    title: 'Tax month',
    controlName: this.controlNames.taxMonth,
    options: [],
    validationMessage: 'Enter tax month',
  };

  taxYearDropDown: SelectControl = {
    title: 'Tax year',
    controlName: this.controlNames.taxYear,
    options: [],
    validationMessage: 'Enter tax year',
  };

   constructor(
    private store: Store,
    private maskAccountNumberPipe: MaskAccountNumberPipe
    ) { }

ngOnInit(): void {
  createControl(
    this.parentForm, 
    this.controlNames.taxPayerId, 
    this.parentForm?.value?.taxPayerId ?? '', 
    [
      Validators.required, Validators.pattern(numeralsOnly), 
      Validators.maxLength(9), 
      Validators.minLength(9)
    ],
    undefined,
    this.useSloa
  );

  if(this.useSloa){
    // Get the value from taxRemittance or the disabled taxPayerId control
    const taxPayerValue = this.taxRemittance?.taxId || this.parentForm?.getRawValue()?.taxPayerId || '';
    const maskedValue = this.maskAccountNumberPipe.transform(taxPayerValue);
    
    createControl(
      this.parentForm, 
      this.controlNames.taxPayerIdMasked, 
      maskedValue, 
      undefined,
      undefined,
      true  // disabled
    );
}
 
  createControl(this.parentForm, this.controlNames.taxTypeCode, this.parentForm?.value?.taxTypeCode ?? '', [Validators.required, Validators.pattern(taxTypeCodeValidation), Validators.maxLength(5), Validators.minLength(5)]);
  if (this.isFromCreateTransaction) {
  createControl(this.parentForm, this.controlNames.taxYear, this.parentForm?.value?.taxYear ?? '', [Validators.required] );
  createControl(this.parentForm, this.controlNames.taxMonth, this.parentForm?.value?.taxMonthCode ?? '', [Validators.required])
  }

  if(this.isFromCreateTransaction){
    this.store.select(selectWireInfo)
    .pipe(takeUntil(this.destroy$))
    .subscribe((wireInfo: WireInfo) => {
      if (wireInfo) {
        this.isIrsAba = wireInfo.isIrsAba;
      }
    });
  }
  else{
    this.store.select(selectStandingAuthWireInfo).pipe(takeUntil(this.destroy$)).subscribe(wireInfo => {
      this.isIrsAba = wireInfo.isIrsAba;
    });
this.store.select(selectIsCreateStandingAuth).pipe(takeUntil(this.destroy$)).subscribe(val => this.isCreateStandingAuthPage = val);
  }

  this.taxMonthDropDown.options = MONTH_LIST.map(month => ({
    value: month.code,
    name: `${month.description} (${month.code})`
  }));

  const currentYear = new Date().getFullYear();
  for (let year = currentYear + 1; year >= 2000; year--) {
    this.taxYearDropDown.options.push({ value: year.toString(), name: year.toString() });
  }
}

 get taxPayer(): UntypedFormControl {
     return this.parentForm.get(this.controlNames.taxPayerId) as UntypedFormControl;
   }

  get maskedTaxPayerId(): UntypedFormControl {
    return this.parentForm.get(this.controlNames.taxPayerIdMasked) as UntypedFormControl;
  }

  get taxType(): UntypedFormControl {
     return this.parentForm.get(this.controlNames.taxTypeCode) as UntypedFormControl;
   }

  get taxPayerError() {
    if (this.taxPayer && this.taxPayer.errors) {
      const errorKeys: string[] = Object.keys(this.taxPayer.errors ?? {});
      if (errorKeys.length > 0) {
        return taxPayerIdErrorMap[errorKeys[0] as keyof typeof taxPayerIdErrorMap] || '';
      }
    }
  }

   get taxTypeCodeError() {
    if (this.taxType && this.taxType.errors) {
      const errorKeys: string[] = Object.keys(this.taxType.errors ?? {});
      if (errorKeys.length > 0) {
        return taxTypeCodeErrorMap[errorKeys[0] as keyof typeof taxTypeCodeErrorMap] || '';
      }
    }
  }

ngOnDestroy(): void {
    this.parentForm?.removeControl(this.controlNames.taxPayerId);
    this.parentForm?.removeControl(this.controlNames.taxTypeCode);
    this.parentForm?.removeControl(this.controlNames.taxMonth);
    this.parentForm?.removeControl(this.controlNames.taxYear);
    this.destroy$.next();
    this.destroy$.complete();
  }


  onPaste(event: ClipboardEvent,  fieldType: 'taxPayerId' | 'taxTypeCode') {
      if (event.clipboardData != undefined) {
        let pastedInput = event.clipboardData.getData('text/plain');
        if(fieldType == 'taxPayerId'){
          if (!numeralsOnly.test(pastedInput)) {
          event.preventDefault();
        }
        }
        else if(fieldType == 'taxTypeCode'){
           if (!taxTypeCodeValidation.test(pastedInput)) {
          event.preventDefault();
        }
        }
        
      }
    }
}
